import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { useSuspendTenant, useActivateTenant } from "@/hooks/useTenantQueries";
import { TenantStatus } from "@/services/admin-tenant.service";
import { useCloseGuard } from "@/hooks/use-close-guard";

// ============================================================================
// Validation Schema
// ============================================================================

const suspensionSchema = z.object({
  reason: z.string().min(10, "Reason must be at least 10 characters").max(500),
});

// Activation asks for no reason. The activate endpoint takes no body — `ActivateTenantCommand`
// carries only the tenant and the admin — so a reason typed here was required and then thrown away.
const activationSchema = z.object({
  reason: z.string(),
});

type SuspensionFormData = z.infer<typeof suspensionSchema>;

// ============================================================================
// Component
// ============================================================================

interface TenantSuspensionModalProps {
  tenantId: string;
  tenantName: string;
  currentStatus: TenantStatus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TenantSuspensionModal({
  tenantId,
  tenantName,
  currentStatus,
  open,
  onOpenChange,
}: TenantSuspensionModalProps) {
  const suspendTenant = useSuspendTenant(tenantId);
  const activateTenant = useActivateTenant(tenantId);

  // The action is fixed when the dialog opens, not read live. A suspend or activate refreshes the
  // tenant before the dialog has finished closing, and a live read flipped the closing dialog —
  // title, alert and the reason box — to the opposite action. Captured during render rather than in
  // an effect, so an open never paints a frame with the previous action.
  const [snapshot, setSnapshot] = React.useState({
    open,
    isSuspending: currentStatus === "Active",
  });
  if (open !== snapshot.open) {
    setSnapshot({
      open,
      isSuspending: open ? currentStatus === "Active" : snapshot.isSuspending,
    });
  }
  const isSuspending =
    open && !snapshot.open ? currentStatus === "Active" : snapshot.isSuspending;

  const form = useForm<SuspensionFormData>({
    resolver: zodResolver(isSuspending ? suspensionSchema : activationSchema),
    defaultValues: {
      reason: "",
    },
  });

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      form.reset({ reason: "" });
    }
  }, [open, form]);

  const onSubmit = async (data: SuspensionFormData) => {
    try {
      if (isSuspending) {
        await suspendTenant.mutateAsync({ reason: data.reason });
      } else {
        await activateTenant.mutateAsync();
      }
      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  };

  const isPending = suspendTenant.isPending || activateTenant.isPending;

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: form.formState.isDirty,
    isSubmitting: isPending,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {isSuspending ? "Suspend Tenant" : "Activate Tenant"}
            </DialogTitle>
          </DialogHeader>

          <DialogBody withoutFooter className="space-y-4">
            <DialogDescription>
              {isSuspending
                ? "Suspending a tenant will disable access to all resources"
                : "Activating a tenant will restore access to all resources"}
            </DialogDescription>
            {/* Warning Alert */}
            <Alert variant={isSuspending ? "destructive" : "default"}>
              {isSuspending ? (
                <AlertTriangle className="h-4 w-4" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                {isSuspending ? (
                  <>
                    <strong>Warning:</strong> Suspending{" "}
                    <strong>{tenantName}</strong> will immediately block all
                    members from accessing their resources. Running VMs will
                    continue to run but cannot be managed.
                  </>
                ) : (
                  <>
                    <strong>Confirm:</strong> Activating{" "}
                    <strong>{tenantName}</strong> will restore full access to
                    all resources and members.
                  </>
                )}
              </AlertDescription>
            </Alert>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <fieldset disabled={isPending} className="space-y-4">
                  {isSuspending && (
                    <FormField
                      control={form.control}
                      name="reason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reason</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="e.g., Payment overdue for 30 days"
                              className="min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Provide a detailed reason for this action (will be
                            logged)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => guard.onOpenChange(false)}
                      disabled={isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant={isSuspending ? "destructive" : "default"}
                      disabled={isPending}
                    >
                      {isPending && <Spinner className="mr-2" />}
                      {isPending
                        ? isSuspending
                          ? "Suspending..."
                          : "Activating..."
                        : isSuspending
                          ? "Suspend Tenant"
                          : "Activate Tenant"}
                    </Button>
                  </DialogFooter>
                </fieldset>
              </form>
            </Form>
          </DialogBody>
        </DialogContent>
      </Dialog>
      {guard.dialog}
      <BusyOverlay
        open={isPending}
        label={isSuspending ? "Suspending tenant…" : "Activating tenant…"}
      />
    </>
  );
}
